import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import FormInput from '../../shared/components/ui/FormInput';
import {
  approveUnderConsideration,
  getAllSalesArsByProjectId,
  getAllSalesTargetsByProjectId,
  getIncentives,
  getUnderConsiderationByProjectID
} from '../services/api/implementation/impl';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import searchLens from '../../shared/assets/svg/searchLens.svg';
import Cookies from 'js-cookie';
import NoDataFoundImg from '../../shared/assets/png/noDataFound.png';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import AiSectionBanner from '../components/ai/AiSectionBanner';

const Performance = () => {
  const userId = Cookies.get('userId');
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const { collapsed } = useSidebar();
  const [month, setMonth] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTask, setSelectedTask] = useState();
  const [stages, setStages] = useState([]);
  const {
    register,
    formState: { errors },
    setValue,
    watch
  } = useForm();
  const projectID = watch('project');
  const [filteredData, setFilteredData] = useState([]);
  const userID = Number(Cookies.get('userId'));

  // Track page view
  useEffect(() => {
    mixpanel.track('Performance Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    const today = new Date();
    // const currentMonth = new Date().getMonth() + 1; // getMonth() is 0-based
    const initialMonth = today.getMonth() + 1;
    setMonth(initialMonth); // could also use month name if needed
  }, []);

  const {
    data: underConsiderationData,
    refetch: refetchUCData,
    isFetching
  } = useQuery({
    queryKey: ['underConsideration', projectID],
    queryFn: () => getUnderConsiderationByProjectID(projectID, 1, 500),
    enabled: !!projectID
  });

  const { data: allARs, refetch: refetchArs } = useQuery({
    queryKey: ['Ars', projectID, month],
    queryFn: () => getAllSalesArsByProjectId(projectID, month),
    enabled: !!projectID && !!month
  });

  const { data: allTargets, refetch: refetTargets } = useQuery({
    queryKey: ['allTargets', projectID, month],
    queryFn: () => getAllSalesTargetsByProjectId(projectID, month),
    enabled: !!projectID && !!month // only runs when both are truthy
  });

  const { data: incentives, refetch: refetchIncentives } = useQuery({
    queryKey: ['incentives', projectID],
    queryFn: () => getIncentives(projectID),
    enabled: !!projectID
  });

  useEffect(() => {
    if (incentives?.data?.[0]) {
      setStages(
        incentives?.data?.[0]?.booking?.map((item) => ({ id: item?.id, label: item?.name }))
      );
    }
  }, [incentives]);

  useEffect(() => {
    setFilteredData(underConsiderationData?.data);
  }, [underConsiderationData?.data]);

  React.useEffect(() => {
    if (projects) {
      setValue('project', projects[0]?.id);
    }
  }, [projects]);

  const handleMonthChange = (value) => {
    // value is 1–12 as passed from the dropdown
    setMonth(value);
  };

  const handleSearchChange = useCallback(
    (incomingQuery) => {
      setSearchQuery(incomingQuery);

      if (incomingQuery) {
        const lowerCaseQuery = incomingQuery.toLowerCase();

        const filtered = underConsiderationData?.data?.filter((row) => {
          const leadName = row.lead?.user?.name?.toLowerCase() || '';
          const unitName = row.unit?.unitName?.toLowerCase() || '';
          const salesName = row.lead?.sales?.name?.toLowerCase() || '';
          const activities = row.lead?.activities;
          const lastActivity =
            Array.isArray(activities) && activities.length > 0
              ? activities[activities.length - 1]?.name?.toLowerCase()
              : '';

          return (
            leadName.includes(lowerCaseQuery) ||
            unitName.includes(lowerCaseQuery) ||
            salesName.includes(lowerCaseQuery) ||
            lastActivity.includes(lowerCaseQuery)
          );
        });

        setFilteredData(filtered);
      } else {
        setFilteredData(underConsiderationData?.data);
      }
    },
    [underConsiderationData?.data]
  );

  const sortedTargets = [...(allTargets?.data || [])].sort((a, b) => {
    if (a.sales_user_id === userID) return -1;
    if (b.sales_user_id === userID) return 1;
    return 0;
  });

  const formatToLakhs = (value) => {
    const num = Number(value) / 100000;
    return (
      num.toLocaleString('en-IN', {
        maximumFractionDigits: 2,
        minimumFractionDigits: num % 1 !== 0 ? 2 : 0
      }) + ' L'
    );
  };

  const { mutate: handleUpdateIncentives } = useMutation({
    mutationFn: (payload) => approveUnderConsideration(selectedTask, payload),
    onSuccess: async () => {
      toast.success('Incentive Stage Updated Successfully');
      setSelectedTask();
      await refetchUCData();
    },
    onError: (err) => {
      console.error('Error updating incentive stage', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error updating incentive stage');
    }
  });

  return (
    <Layout>
      {/* Header */}
      <div
        className={`fixed top-0 z-10 transition-all duration-300 bg-white ${
          collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
        }`}>
        <Header data={{ name: `Performance` }} />
        <div
          className={`flex justify-between items-end gap-2 fixed bg-white border px-6 py-3 pb-6 mt-[-0.60rem] z-30 transition-all duration-300 ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <div className="flex gap-3 w-[50%] justify-start items-center">
            <FormInput
              width={50}
              label={''}
              register={register}
              register_key={'project'}
              isRequired={false}
              type={'dropdown'}
              options={projectsForDropdown}
              validation={{ required: 'Project is required' }}
              errors={errors}
            />
            <FormInput
              width={26}
              register={register}
              register_key={'month'}
              label={''}
              type={'date-month'}
              isRequired={false}
              onType={(mo) => {
                handleMonthChange(mo.id);
              }}
              valuePre={month}
            />
          </div>
        </div>
      </div>

      {/* Main Section */}
      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="px-5 pt-[10rem]">
          <AiSectionBanner projectId={projectID} vertical="sales" surface="performance" />
        </div>
        <div className="flex items-start gap-7 pl-5 pr-4 justify-start">
          <div className="flex flex-col w-[40%] border-r-2 border-gray-200">
            {sortedTargets?.length > 0 ? (
              sortedTargets?.map((user, index) => (
                <div key={index} className="mb-4">
                  <div className="text-lg font-medium mb-2">
                    {user.sales_user_id === userID ? 'Me' : user?.user?.name}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <div className="bg-white rounded-xl p-4 w-full min-w-[220px] max-w-fit shadow flex h-[5rem] gap-3">
                      <div className="flex flex-col justify-center items-center w-[51%]">
                        <div className="text-xl font-semibold text-black ">
                          ₹{formatToLakhs(user.target_amount)}
                        </div>
                        <div className="text-gray-600 mb-1">Bookings</div>
                      </div>
                      <div className="bg-[#C8C8C8] p-[1px]"></div>
                      <div className="flex flex-col justify-center text-xs text-gray-600 w-[100%] gap-2">
                        <div className="flex justify-between items-center w-[100%]">
                          <div className="font-medium text-gray-700">Achieved:</div>
                          <div className="text-black">
                            ₹{formatToLakhs(parseInt(user.booking_achieved))}
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="font-medium text-gray-700">Pending:</div>
                          <div className="text-black">
                            ₹
                            {formatToLakhs(
                              parseInt(user.target_amount) - parseInt(user.booking_achieved)
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-4 w-full min-w-[220px] max-w-fit shadow flex h-[5rem] gap-3">
                      <div className="flex flex-col justify-center items-center">
                        <div className="text-xl font-semibold text-black ">
                          ₹{formatToLakhs(parseInt(user.ar_target))}
                        </div>
                        <div className="text-gray-600 mb-1 text-center text-[0.7rem] text-nowrap">
                          AR TARGET
                        </div>
                      </div>
                      <div className="bg-[#C8C8C8] p-[1px]"></div>
                      <div className="flex flex-col justify-center text-xs text-gray-600 w-[100%] gap-2">
                        <div className="flex justify-between items-center w-[100%]">
                          <div className="font-medium text-gray-700">Achieved:</div>
                          <div className="text-black">
                            ₹{formatToLakhs(parseInt(user.monthly_target))}
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="font-medium text-gray-700">Pending:</div>
                          <div className="text-black">
                            ₹
                            {formatToLakhs(
                              parseInt(user.ar_target) - parseInt(user.monthly_target)
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-4 w-full min-w-[220px] max-w-fit shadow flex h-[5rem] gap-3">
                      <div className="flex flex-col justify-center items-center">
                        <div className="text-gray-600 mb-1 text-center text-[0.7rem]">
                          INCENTIVE EARNED
                        </div>
                      </div>
                      <div className="bg-[#C8C8C8] p-[1px]"></div>
                      <div className="flex flex-col justify-center text-xs text-gray-600 w-[100%] gap-2">
                        <div className="flex justify-between items-center w-[100%]">
                          <div className="font-medium text-gray-700">Bookings incentive:</div>
                          <div className="text-black">
                            ₹{parseInt(user.bookingincentive_achieved).toLocaleString('en-IN')}
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="font-medium text-gray-700">AR incentive:</div>
                          <div className="text-black">
                            ₹{parseInt(user.achived_ar_incentive).toLocaleString('en-IN')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* <div className="text-lg mt-4 bg-white p-1 rounded-lg justify-center items-center flex gap-3 w-[92.5%]">
                Incentive Allowed <img className="h-[1rem], w-[2rem]" src={ArrowIcon} alt="" />
                <strong>10%</strong>
              </div> */}
                  <hr className="my-5 border-gray-300 w-[92.5%]" />
                </div>
              ))
            ) : (
              <div className="h-10">
                <img
                  src={NoDataFoundImg} // Replace this with your actual image URL
                  alt="No Data"
                  className="w-[65%] mx-auto mb-4 object-contain"
                />
              </div>
            )}
          </div>
          <div
            className={`fixed transition-all duration-300 ${collapsed ? 'w-[55%]' : 'w-[48%]'} bg-gray-100 rounded-md p-4 right-3`}
            style={{ height: '75vh' }}>
            {/* Fixed Search Bar */}
            <div className="p-2 flex justify-between items-center rounded-full bg-white pr-3 mb-4">
              <input
                type="text"
                placeholder="Search Lead"
                className="px-3 py-1.5 text-sm w-[80%] focus:outline-none"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
              <img src={searchLens} alt="Search" />
            </div>

            {/* Scrollable Table Container */}
            <div className="overflow-y-auto" style={{ height: 'calc(75vh - 4.6rem)' }}>
              {filteredData && filteredData?.length > 0 ? (
                <table className="w-full text-sm bg-white rounded shadow">
                  {/* Fixed Table Header */}
                  <thead className="sticky top-0 bg-gray-100 z-10">
                    <tr>
                      <th className="text-left p-3">Lead Name</th>
                      <th className="text-left p-3">Unit Name</th>
                      <th className="text-left p-3">Sales Agent</th>
                      <th className="text-left p-3">Incentive Stage</th>
                      <th className="text-left p-3">Converted Date</th>
                    </tr>
                  </thead>

                  {/* Scrollable Table Body */}
                  <tbody>
                    {filteredData?.map((row, idx) => {
                      const activities = row.lead?.activities;
                      const lastActivity =
                        Array.isArray(activities) && activities.length > 0
                          ? activities[activities.length - 1]?.name
                          : '---';

                      const formattedDate = row.created_at
                        ? new Date(row.created_at).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })
                        : '---';
                      const currentStageIndex = stages.findIndex(
                        (stage) => stage.id === row?.newbooking_id
                      );
                      const currentStage = stages[currentStageIndex]?.label || '---';
                      const remainingStages = stages.slice(currentStageIndex + 1);

                      return (
                        <tr key={idx} className="border-t border-gray-200">
                          <td className="p-3">{row.lead?.user?.name || '---'}</td>
                          <td className="p-3">{(row?.unit?.unitName || '---').trim()}</td>
                          <td className="p-3">{row.lead?.sales?.name || '---'}</td>
                          <td className="p-3">
                            {row.newBookingIncentive?.name ? (
                              remainingStages?.length > 0 ? (
                                <div className="flex flex-row gap-1 cursor-pointer">
                                  <select
                                    className="border-none p-1 text-sm bg-transparent focus:outline-none focus:ring-0 cursor-pointer"
                                    onChange={(e) => {
                                      setSelectedTask(row.id);
                                      const selectedId = Number(e.target.value);
                                      handleUpdateIncentives({ newbooking_id: selectedId });
                                    }}
                                    value={row?.newbooking_id || ''}>
                                    <option value={row?.newbooking_id || ''}>{currentStage}</option>
                                    {remainingStages.map((stage) => (
                                      <option key={stage.id} value={stage.id}>
                                        {stage.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              ) : (
                                row.newBookingIncentive?.name
                              )
                            ) : (
                              '---'
                            )}
                          </td>
                          <td className="p-3">{formattedDate}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="h-10">
                  <img
                    src={NoDataFoundImg} // Replace this with your actual image URL
                    alt="No Data"
                    className="w-[65%] mx-auto mb-4 object-contain"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Performance;
